import { useState } from "react";
import { Pencil, Star, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../api/client";
import { ConfirmButton } from "../components/ConfirmButton";
import { backgroundStyle } from "../util/backgrounds";
import type { CreateBoardRequest } from "@kandoo/shared";

export function BoardsPage() {
  const qc = useQueryClient();
  const { data: boards, isLoading } = useQuery({
    queryKey: ["boards"],
    queryFn: () => api.boards.list(),
  });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const createMutation = useMutation({
    mutationFn: (b: CreateBoardRequest) => api.boards.create(b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["boards"] });
      setTitle("");
      setDescription("");
    },
  });
  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.boards.update(id, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["boards"] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.boards.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["boards"] }),
  });
  const starMutation = useMutation({
    mutationFn: ({ id, starred }: { id: string; starred: boolean }) =>
      starred ? api.boards.star(id) : api.boards.unstar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["boards"] }),
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    createMutation.mutate({
      title,
      description: description || undefined,
    });
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-4 text-2xl font-semibold">Your boards</h1>

      <form
        onSubmit={onSubmit}
        className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-[var(--surface)] p-4"
      >
        <label className="flex-1 text-sm">
          <span className="text-slate-600">New board title</span>
          <input
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Product roadmap"
          />
        </label>
        <label className="flex-1 text-sm">
          <span className="text-slate-600">Description (optional)</span>
          <input
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={createMutation.isPending}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {createMutation.isPending ? "Creating…" : "Create board"}
        </button>
        {createMutation.isError && (
          <p className="text-sm text-red-600">
            {(createMutation.error as Error).message}
          </p>
        )}
      </form>

      {isLoading ? (
        <p className="text-slate-500">Loading boards…</p>
      ) : boards && boards.length > 0 ? (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[...boards]
            .sort((a, b) => Number(b.starred ?? false) - Number(a.starred ?? false))
            .map((b) => (
            <li
              key={b.id}
              className="group overflow-hidden rounded-lg border border-slate-200 bg-[var(--surface)] transition hover:border-blue-400 hover:shadow"
            >
              {/* background preview strip (subtle placeholder when unset) */}
              <Link
                to={`/boards/${b.id}`}
                aria-label={`Open ${b.title}`}
                tabIndex={-1}
                className="block h-14 bg-slate-100"
                style={backgroundStyle(b.background)}
              />
              <div className="flex items-start justify-between gap-2 p-4">
                <Link
                  to={`/boards/${b.id}`}
                  className="flex-1 font-medium text-slate-900 hover:text-blue-600"
                >
                  {b.title}
                </Link>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      starMutation.mutate({ id: b.id, starred: !b.starred })
                    }
                    aria-label={b.starred ? "Unstar board" : "Star board"}
                    className={`rounded px-1.5 py-1 transition hover:bg-amber-50 ${
                      b.starred
                        ? "text-amber-500"
                        : "text-slate-400 opacity-0 hover:text-amber-500 group-hover:opacity-100"
                    }`}
                    title={b.starred ? "Unstar board" : "Star board"}
                  >
                    <Star size={12} className={b.starred ? "fill-current" : ""} />
                  </button>
                  <div className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => {
                        const next = window.prompt("Rename board", b.title);
                        if (next && next.trim() && next !== b.title) {
                          renameMutation.mutate({ id: b.id, title: next.trim() });
                        }
                      }}
                      className="rounded px-1.5 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      title="Rename board"
                    >
                      <Pencil size={12} />
                    </button>
                    <ConfirmButton
                      onConfirm={() => deleteMutation.mutate(b.id)}
                      title="Delete board"
                      className="rounded px-1.5 py-1 text-xs text-slate-400 hover:bg-red-50 hover:text-red-600"
                      confirmClassName="rounded px-1.5 text-xs bg-red-600 text-white"
                      confirmLabel="Delete?"
                    >
                      <X size={12} />
                    </ConfirmButton>
                  </div>
                </div>
              </div>
              {b.description && (
                <div className="-mt-2 px-4 pb-4 text-sm text-slate-500">
                  {b.description}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-slate-500">
          No boards yet — create your first one above.
        </p>
      )}
    </div>
  );
}